<template>
  <div :class="$attrs.class" :style="$attrs.style as any">
    <LeadCombobox
      :label="props.label"
      :required="props.required"
      :placeholder="props.placeholder"
      :items="availableLocations"
      getOptionLabel="formatted_address"
      getOptionValue="formatted_address"
      :modelValue="selectedOption"
      @update:modelValue="handlePositionTargetingUpdate"
      @search="onSearch"
      @open="handleOpen"
      :error="errors"
      :disabled="props.disabled"
      :loading="isLoading"
      :disableFiltering="true"
      :displayInitials="props.displayInitials"
      v-bind="filteredAttrs">
      <template #hint v-if="props.hint">{{ props.hint }}</template>
    </LeadCombobox>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, useAttrs } from 'vue';
import { request } from '@/api/client';
import type { IGoogleAddress } from '@/types/address';
import { debounce } from '@prospective/lithium';
import LeadCombobox from '@/components/form/LeadCombobox.vue';

/* ---------------------------------- PROPS --------------------------------- */
const modelValue = defineModel<any>({ required: true });
const props = withDefaults(
  defineProps<{
    label?: string;
    placeholder?: string;
    hint?: string;
    errors?: string[];
    required?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    displayInitials?: boolean;
  }>(),
  {
    placeholder: 'Search for address',
    errors: () => [],
    required: true,
    clearable: false,
    disabled: false,
  }
);
const emit = defineEmits<{
  (e: 'updated', value: IGoogleAddress | null): void;
}>();

defineOptions({ inheritAttrs: false });

const attrs = useAttrs();
const filteredAttrs = computed(() => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { class: className, style, ...rest } = attrs;
  return rest;
});
/* -------------------------------- STATE -------------------------------- */

const availableLocations = ref<IGoogleAddress[]>([]);
const isLoading = ref(false);

/* --------------------------------- LIFECYCLE -------------------------------- */

onMounted(() => {
  if (modelValue.value) {
    geoCodeAddress(modelValue.value);
  }
});

/* --------------------------------- COMPUTED -------------------------------- */

const selectedOption = computed(() => {
  const street = modelValue.value;

  if (!street) {
    return null;
  }

  const found = availableLocations.value.find((loc) => loc.formatted_address === street);
  if (found) {
    return found;
  }

  return {
    formatted_address: street,
  };
});

/* ---------------------------------- METHODS --------------------------------- */

function handlePositionTargetingUpdate(gAddress: IGoogleAddress | null) {
  if (!gAddress) {
    modelValue.value = null;
    emit('updated', null);
    return;
  }
  modelValue.value = gAddress.formatted_address;
  emit('updated', gAddress);
}

const geoCodeAddressDebounced = debounce(geoCodeAddress, 500);

function onSearch(query: string) {
  isLoading.value = true;
  geoCodeAddressDebounced(query);
}

function handleOpen() {
  if (modelValue.value && availableLocations.value.length === 0) {
    geoCodeAddress(modelValue.value);
  }
}

function geoCodeAddress(address: string) {
  if (!address) {
    availableLocations.value = [];
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  request('POST', '/v1/account/google-maps/geo-coding', undefined, { address })
    .then((response: { data: { results: IGoogleAddress[] } }) => {
      availableLocations.value = response.data.results ?? [];
    })
    .catch((e) => {
      console.error(e);
    })
    .finally(() => {
      isLoading.value = false;
    });
}
</script>
